import {
  CREDIT_CARD_ENUM,
  DEBIT_CARD_ENUM,
  BANK_TRANSFER_ENUM,
  SAFETY_PAY_ENUM,
  PAYMENT_METHOD_ENUM,
  isPix,
  isBoleto
} from '@/enums/payment-method.enum'

import MathManager from '@/utils/math-manager.utils'
import TextManager from '@/utils/text-manager.utils'

import PAYMENT_METHODS_LABELS from '../PaymentMethods/payment-methods.labels'

class ConversionManager {
  list

  boleto = PAYMENT_METHOD_ENUM.BOLETO.toLowerCase()
  pix = PAYMENT_METHOD_ENUM.PIX.toLocaleLowerCase()

  constructor (list) {
    this.list = list
  }

  defaultStructure = ['cards', this.boleto, this.pix]

  cardNames = [
    CREDIT_CARD_ENUM.TYPE,
    PAYMENT_METHOD_ENUM.VOUCHER.toLowerCase(),
    DEBIT_CARD_ENUM.TYPE
  ]

  othersNames = [
    BANK_TRANSFER_ENUM.TYPE,
    SAFETY_PAY_ENUM.TYPE,
    PAYMENT_METHOD_ENUM.CASH.toLowerCase()
  ]

  /**
   * parseLabel takes in a single parameter label and returns a string value by matching the label parameter
   * with the key of an object validationLabel.
   * The validationLabel object has properties that are string keys, such as 'boleto',
   * 'credit_card', 'bank_transfer', etc., and their values are string as well, such as 'conversions.boleto',
   * 'conversions.credit-card', 'payment-method.bank-transfer' etc.
   * @param {String} label the label to convert
   * @returns String
   */
  parseLabel (label) {
    const validationLabel = {
      boleto: 'conversion.boleto',
      credit_card: 'conversion.credit-card',
      debit_card: 'conversion.debit',
      cash: 'conversion.cash',
      voucher: 'conversion.voucher',
      pix: 'conversion.pix',
      bank_transfer: 'conversion.bank-transfer',
      safety_pay: 'conversion.safety-pay',
      others: 'conversion.others',
      cards: 'conversion.cards'
    }
    return validationLabel[label] ?? TextManager.formatTextSnakeOrCamel(label)
  }

  parseLabelTooltip (label) {
    const validationLabel = {
      ...PAYMENT_METHODS_LABELS,
      debit: 'payment-method.debit'
    }
    return validationLabel[label] ?? TextManager.formatTextSnakeOrCamel(label)
  }

  defaultAdapter (object) {
    return {
      ...object,
      color: '#8267D3',
      percentage: MathManager.formatConversionNumber(object?.conversion ?? 0)
    }
  }

  boletoAdapter (object) {
    const ICON = 'boleto'

    if (!object)
      return this.defaultAdapter({
        key: this.boleto,
        conversion: 0,
        name: this.parseLabel(this.boleto),
        leftIcon: {
          name: ICON,
          tooltip: null
        }
      })

    return {
      key: this.boleto,
      leftIcon: {
        name: ICON,
        tooltip: null
      },
      ...this.defaultAdapter(object),
      name: this.parseLabel(object.name)
    }
  }

  pixAdapter (object) {
    const ICON = 'pix'

    if (!object)
      return this.defaultAdapter({
        key: this.pix,
        conversion: 0,
        name: this.parseLabel(this.pix),
        leftIcon: {
          name: ICON,
          tooltip: null
        }
      })

    return {
      key: this.pix,
      leftIcon: {
        name: ICON,
        tooltip: null
      },
      ...this.defaultAdapter(object),
      name: this.parseLabel(object.name)
    }
  }

  cardAdapter (object) {
    const KEY = 'cards'
    const ICON = 'credit_card'

    if (!object)
      return this.defaultAdapter({
        key: KEY,
        conversion: 0,
        leftIcon: {
          name: ICON,
          tooltip: null
        },
        name: this.parseLabel(KEY)
      })

    return {
      key: KEY,
      leftIcon: {
        name: ICON,
        tooltip: null
      },
      rightIcon: {
        name: 'cards_tooltip',
        tooltip: [
          {
            name: 'conversion.cards'
          }
        ]
      },
      ...this.defaultAdapter(object),
      name: this.parseLabel(KEY)
    }
  }

  othersAdapter (others, conversion = 0) {
    const KEY = 'others'
    const ICON = 'others'

    if (!others)
      return this.defaultAdapter({
        conversion,
        key: KEY,
        name: this.parseLabel(KEY),
        leftIcon: {
          name: ICON,
          tooltip: null
        }
      })

    return {
      conversion,
      key: KEY,
      color: '#8267D3',
      name: this.parseLabel(KEY),
      leftIcon: {
        name: ICON,
        tooltip: null
      },
      rightIcon: {
        name: 'others_tooltip',
        tooltip: others.map(other => ({
          ...this.defaultAdapter(other),
          name: this.parseLabelTooltip(other.name)
        }))
      },
      percentage: MathManager.formatConversionNumber(conversion ?? 0) || 0
    }
  }

  adapter (cards, boleto, pix, others) {
    const quantities = cards.map(item => item.quantity)
    const conversions = cards.map(item => item.conversion)
    const rate = MathManager.calculateConversionRate(quantities, conversions)
    const card = { conversion: rate }
    const cardConversion = this.cardAdapter(card)

    const boletoConversion = this.boletoAdapter(boleto)

    const pixConversion = this.pixAdapter(pix)

    const quantitiesOthers = others.map(item => item.quantity)
    const conversionsOthers = others.map(item => item.conversion)
    const rateOthers = MathManager.calculateConversionRate(
      quantitiesOthers,
      conversionsOthers
    )
    const othersConversion = this.othersAdapter(others, rateOthers)

    return [cardConversion, boletoConversion, pixConversion, othersConversion]
  }

  /**
   * Executes all the methods
   * @returns Array
   */
  get () {
    const cards = this.list.filter(paymentMethod =>
      this.cardNames.includes(paymentMethod.name)
    )
    const boleto = this.list.find(paymentMethod =>
      isBoleto(paymentMethod.name?.toLowerCase())
    )

    const pix = this.list.find(paymentMethod =>
      isPix(paymentMethod.name?.toLowerCase())
    )

    const others = this.list.filter(
      paymentMethod =>
        this.othersNames.includes(paymentMethod.name) &&
        paymentMethod.conversion > 0
    )
    return this.adapter(cards, boleto, pix, others)
  }
}

export default ConversionManager
